import React from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";
import { isObject } from "lodash";

import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeToc from "rehype-toc";
import rehypeSlug from "rehype-slug";

import MediaWrapper from "@/components/mediaWrapper";
import LinkWrapper from "@/components/linkWrapper";

export const query = graphql`
  fragment StrapiSharedBody on StrapiComponentSharedBody {
    text
    images {
      url
      ...StrapiMediaSubquery
    }
  }
`;

function MarkdownWrapper({
  text,
  images,
  className,
  markdownClassName,
  linkClassName,
  imgDivClassName,
  imgClassName,
  useImgCaption = false,
  useToc = false,
  tocTitle = "",
  tocOptions = {},
  replaceParagraph = true,
  replaceComponents = {},
  blockHtml = false,
  ...other
}) {
  const query = useStaticQuery(
    graphql`
      query markdownWrapper {
        site {
          siteMetadata {
            allowHtml
          }
        }
      }
    `
  );
  const allowHtml = !blockHtml && query?.site?.siteMetadata?.allowHtml;

  if (text) {
    const newMarkdownClassName = ["", className, markdownClassName].join(" ");
    const newImgDivClassName = imgDivClassName ? imgDivClassName : "w-full";
    const newImgClassName = imgClassName ? imgClassName : "rounded-lg";

    const components = {};
    if (isObject(replaceComponents)) {
      for (let [source, target] of Object.entries(replaceComponents)) {
        components[source] = target;
      }
    }

    components["p"] = ({ children, node }) => {
      if (
        replaceParagraph ||
        node.children?.some((child) => child.tagName === "img")
      ) {
        return <span>{children}</span>;
      }
      return <p>{children}</p>;
    };

    components["a"] = ({ href, children }) => {
      return (
        <LinkWrapper
          to={href}
          className={
            linkClassName
              ? linkClassName
              : "text-link-color underline visited:text-link-visited hover:text-link-hover"
          }
        >
          {children}
        </LinkWrapper>
      );
    };
    if (images?.length && images[0].url) {
      components["img"] = ({
        src,
        alt,
        className,
        divclassname,
        objectfit,
        objectposition,
      }) => {
        const image = images.find((element) => element.url === src);
        if (image) {
          className = className ? className : newImgClassName;
          let divClassName = divclassname ? divclassname : newImgDivClassName;
          let objectFit = objectfit ? objectfit : "contain";
          let objectPosition = objectposition ? objectposition : "50% 50%";
          return (
            <figure>
              <MediaWrapper
                alt={alt}
                divClassName={divClassName}
                className={className}
                objectFit={objectFit}
                objectPosition={objectPosition}
                media={image}
                as="span"
                {...other}
              />
              {useImgCaption && <figcaption>{alt}</figcaption>}
            </figure>
          );
        } else {
          return <template />;
        }
      };
    }

    return (
      <>
        {useToc && tocTitle && (
          <h3 className={`${newMarkdownClassName} break-words`}>{tocTitle}</h3>
        )}
        <ReactMarkdown
          className={`${newMarkdownClassName} break-words`}
          components={components}
          remarkPlugins={[remarkBreaks]}
          rehypePlugins={[
            ...(allowHtml ? [rehypeRaw] : []),
            ...(useToc ? [rehypeSlug, [rehypeToc, tocOptions]] : []),
          ]}
        >
          {text}
        </ReactMarkdown>
      </>
    );
  } else {
    return <template />;
  }
}

MarkdownWrapper.propTypes = {
  text: PropTypes.string,
  images: PropTypes.array,
  className: PropTypes.string,
  markdownClassName: PropTypes.string,
  linkClassName: PropTypes.string,
  imgDivClassName: PropTypes.string,
  imgClassName: PropTypes.string,
  useImgCaption: PropTypes.bool,
  useToc: PropTypes.bool,
  tocTitle: PropTypes.string,
  tocOptions: PropTypes.object,
  replaceParagraph: PropTypes.bool,
  replaceComponents: PropTypes.object,
};

export default MarkdownWrapper;
